import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import * as websocketService from '../services/websocketService';
import notificationService from '../services/notificationService';

// Socket Context Types
interface SocketContextType {
  isConnected: boolean;
  connectionState: number;
  socketService: typeof websocketService;
  websocketService: typeof websocketService;
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: any) => void;
  sendGameList: (gameList: number[]) => void;
  sendPublicLogin: (token: string) => void;
  sendRoomType: (pageInfo: any) => void;
  on: (event: string, handler: Function) => void;
  off: (event: string, handler?: Function) => void;
  // Enhanced event handlers
  onForceLogout: (handler: Function) => void;
  onAutoLogoutWarning: (handler: Function) => void;
  onMaintenanceMode: (handler: Function) => void;
  onDuplicateConnection: (handler: Function) => void;
  onPriceUpdate: (handler: Function) => void;
  onLiveScoreUpdate: (handler: Function) => void;
  onSportDataUpdate: (handler: Function) => void;
  onNoticeUpdate: (handler: Function) => void;
  onUserInfoUpdate: (handler: Function) => void;
  onQuestionListUpdate: (handler: Function) => void;
  onNoteListUpdate: (handler: Function) => void;
  // Status methods
  isMaintenanceMode: () => boolean;
  hasDuplicateConnectionWarning: () => boolean;
  getSocketQueueLength: () => number;
  getSportsQueueKeys: () => string[];
  clearSportsQueue: () => void;
  getReconnectSettings: () => any;
  getHeartBitTime: () => number;
  getRequestSeed: () => number;
  resetRequestSeed: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: React.ReactNode;
  autoConnect?: boolean;
  token?: string | null;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ 
  children, 
  autoConnect = true,
  token = null 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState(WebSocket.CLOSED);
  const [isInitialized, setIsInitialized] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const noteListSocketRef = useRef<ReturnType<typeof io> | null>(null);

  // Socket.IO connection to main backend for noteList (broadcast messages).
  // Backend sends noteList via Socket.IO; main app uses raw WebSocket, so we bridge here.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      if (noteListSocketRef.current) {
        noteListSocketRef.current.disconnect();
        noteListSocketRef.current = null;
      }
      return;
    }
    const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const socket = io(apiUrl, { transports: ['websocket', 'polling'], path: '/socket.io/' });
    noteListSocketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('user-join', { userId: String(user.id) });
    });
    socket.on('noteList', (data: any) => {
      websocketService.triggerNoteList(data);
      const msg = data?.message;
      const title = (msg?.title || msg?.message) ? String(msg.title || msg.message) : 'New message';
      const body = (msg?.content || msg?.description) ? String(msg.content || msg.description) : 'You have a new notification.';
      notificationService.notice(title, body, 6000);
    });
    return () => {
      socket.disconnect();
      noteListSocketRef.current = null;
    };
  }, [isAuthenticated, user?.id]);

  // Initialize socket service
  useEffect(() => {
    if (!isInitialized) {
      // Update token if provided
      if (token) {
        websocketService.updateToken(token);
      }
      
      // Set up event listeners
      websocketService.on('connected', () => {
        setIsConnected(true);
        setConnectionState(WebSocket.OPEN as 3);
        console.log('Socket connected via context');
      });

      websocketService.on('disconnected', () => {
        setIsConnected(false);
        setConnectionState(WebSocket.CLOSED);
        console.log('Socket disconnected via context');
      });

      websocketService.on('error', (error: any) => {
        // console.error('Socket error via context:', error);
        setIsConnected(false);
        setConnectionState(WebSocket.CLOSED);
      });

      websocketService.on('maxReconnectAttemptsReached', () => {
        // console.error('Max reconnection attempts reached');
        setIsConnected(false);
        setConnectionState(WebSocket.CLOSED);
      });

      setIsInitialized(true);
    }
  }, [isInitialized, token]);

  const connect = useCallback(async () => {
    try {
      await websocketService.connect();
    } catch (error) {
      // console.error('Failed to connect socket:', error);
      // Don't re-throw to prevent uncaught promise rejection
    }
  }, []);

  // Auto-connect if enabled
  useEffect(() => {
    if (isInitialized && autoConnect && !isConnected) {
      connect().catch(() => {
        // Silently handle connection error - already logged in connect()
      });
    }
  }, [isInitialized, autoConnect, isConnected, connect]);

  // Connection state monitoring
  useEffect(() => {
    if (isInitialized) {
      connectionCheckIntervalRef.current = setInterval(() => {
        const currentState = websocketService.getConnectionState();
        setConnectionState(currentState as 3);
        setIsConnected(currentState === WebSocket.OPEN);
      }, 1000);

      return () => {
        if (connectionCheckIntervalRef.current) {
          clearInterval(connectionCheckIntervalRef.current);
        }
      };
    }
  }, [isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    const reconnectTimeout = reconnectTimeoutRef.current;
    const connectionCheckInterval = connectionCheckIntervalRef.current;
    
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setIsConnected(false);
    setConnectionState(WebSocket.CLOSED);
  }, []);

  const sendMessage = useCallback((message: any) => {
    websocketService.send(message);
  }, []);

  const sendGameList = useCallback((gameList: number[]) => {
    websocketService.sendGameList(gameList);
  }, []);

  const sendPublicLogin = useCallback((token: string) => {
    websocketService.sendPublicLogin(token);
  }, []);

  const sendRoomType = useCallback((pageInfo: any) => {
    websocketService.sendRoomType(pageInfo);
  }, []);

  const on = useCallback((event: string, handler: Function) => {
    websocketService.on(event, handler);
  }, []);

  const off = useCallback((event: string, handler?: Function) => {
    websocketService.off(event, handler);
  }, []);

  // Enhanced event handlers
  const onForceLogout = useCallback((handler: Function) => {
    websocketService.onForceLogout(handler);
  }, []);

  const onAutoLogoutWarning = useCallback((handler: Function) => {
    websocketService.onAutoLogoutWarning(handler);
  }, []);

  const onMaintenanceMode = useCallback((handler: Function) => {
    websocketService.onMaintenanceMode(handler);
  }, []);

  const onDuplicateConnection = useCallback((handler: Function) => {
    websocketService.onDuplicateConnection(handler);
  }, []);

  const onPriceUpdate = useCallback((handler: Function) => {
    websocketService.onPriceUpdate(handler);
  }, []);

  const onLiveScoreUpdate = useCallback((handler: Function) => {
    websocketService.onLiveScoreUpdate(handler);
  }, []);

  const onSportDataUpdate = useCallback((handler: Function) => {
    websocketService.onSportDataUpdate(handler);
  }, []);

  const onNoticeUpdate = useCallback((handler: Function) => {
    websocketService.onNoticeUpdate(handler);
  }, []);

  const onUserInfoUpdate = useCallback((handler: Function) => {
    websocketService.onUserInfoUpdate(handler);
  }, []);

  const onQuestionListUpdate = useCallback((handler: Function) => {
    websocketService.onQuestionListUpdate(handler);
  }, []);

  const onNoteListUpdate = useCallback((handler: Function) => {
    websocketService.onNoteListUpdate(handler);
  }, []);

  // Status methods
  const isMaintenanceMode = useCallback(() => {
    return websocketService.isMaintenanceMode();
  }, []);

  const hasDuplicateConnectionWarning = useCallback(() => {
    return websocketService.hasDuplicateConnectionWarning();
  }, []);

  const getSocketQueueLength = useCallback(() => {
    return websocketService.getSocketQueueLength();
  }, []);

  const getSportsQueueKeys = useCallback(() => {
    return websocketService.getSportsQueueKeys();
  }, []);

  const clearSportsQueue = useCallback(() => {
    websocketService.clearSportsQueue();
  }, []);

  const getReconnectSettings = useCallback(() => {
    return websocketService.getReconnectSettings();
  }, []);

  const getHeartBitTime = useCallback(() => {
    return websocketService.getHeartBitTime();
  }, []);

  const getRequestSeed = useCallback(() => {
    return websocketService.getRequestSeed();
  }, []);

  const resetRequestSeed = useCallback(() => {
    websocketService.resetRequestSeed();
  }, []);

  const contextValue: SocketContextType = {
    isConnected,
    connectionState,
    socketService: websocketService,
    websocketService: websocketService,
    connect,
    disconnect,
    sendMessage,
    sendGameList,
    sendPublicLogin,
    sendRoomType,
    on,
    off,
    onForceLogout,
    onAutoLogoutWarning,
    onMaintenanceMode,
    onDuplicateConnection,
    onPriceUpdate,
    onLiveScoreUpdate,
    onSportDataUpdate,
    onNoticeUpdate,
    onUserInfoUpdate,
    onQuestionListUpdate,
    onNoteListUpdate,
    isMaintenanceMode,
    hasDuplicateConnectionWarning,
    getSocketQueueLength,
    getSportsQueueKeys,
    clearSportsQueue,
    getReconnectSettings,
    getHeartBitTime,
    getRequestSeed,
    resetRequestSeed,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook to use socket context
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Hook for socket connection status
export const useSocketConnection = () => {
  const { isConnected, connectionState, connect, disconnect } = useSocket();
  
  return {
    isConnected,
    connectionState,
    connect,
    disconnect,
    isConnecting: connectionState === WebSocket.CONNECTING,
    isDisconnected: connectionState === WebSocket.CLOSED,
  };
};

// Hook for socket events
export const useSocketEvents = () => {
  const { on, off, onForceLogout, onAutoLogoutWarning, onMaintenanceMode, onDuplicateConnection, onPriceUpdate, onLiveScoreUpdate, onSportDataUpdate, onNoticeUpdate, onUserInfoUpdate, onQuestionListUpdate, onNoteListUpdate } = useSocket();
  
  return {
    on,
    off,
    onForceLogout,
    onAutoLogoutWarning,
    onMaintenanceMode,
    onDuplicateConnection,
    onPriceUpdate,
    onLiveScoreUpdate,
    onSportDataUpdate,
    onNoticeUpdate,
    onUserInfoUpdate,
    onQuestionListUpdate,
    onNoteListUpdate,
  };
};

// Hook for socket status
export const useSocketStatus = () => {
  const { 
    isMaintenanceMode, 
    hasDuplicateConnectionWarning, 
    getSocketQueueLength, 
    getSportsQueueKeys, 
    clearSportsQueue, 
    getReconnectSettings, 
    getHeartBitTime, 
    getRequestSeed, 
    resetRequestSeed 
  } = useSocket();
  
  return {
    isMaintenanceMode,
    hasDuplicateConnectionWarning,
    getSocketQueueLength,
    getSportsQueueKeys,
    clearSportsQueue,
    getReconnectSettings,
    getHeartBitTime,
    getRequestSeed,
    resetRequestSeed,
  };
};

export default SocketContext;
